import { Injectable } from '@angular/core';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../external-interfaces/notification-provider.service';
import SlotModel from '../models/slot.model';
import SlotFirstPageModel from '../models/slot-first-page.model';
import SlotSecondPageModel from '../models/slot-second-page.model';
import DataSlotModel from '../models/data-slot.model';
import DataSlotFirstPageModel from '../models/data-slot-first-page.model';
import { BundleTypeEnum } from '../models/cartDetails/bundle-type.enum';
import { ActionTypeEnum } from '../models/cartDetails/actionType.model';
import { DeviceModel } from '../models/device.model';
import { AccessorySecondPageModel } from '../models/accessory-second-page.model';
import { AccessoryFirstPageModel } from '../models/accessory-first-page.model';
import { CommonGadgetsService } from '../customer-offer/order-gadgets/common-gadgets.service';
import { CommonInfoService } from './common-info.service';
import { IAdditionalBundleSet, IBundle, ICartDetails, IDevice } from '../interfaces/cart-details.interface';
import { DataLayerService } from '../customer-actions/data-layer.service';
import { CommonAddonsService } from '../customer-offer/order-addons/common-addons.service';
import { ProcessTypeEnum } from '../models/cartDetails/process-type.enum';
import { IExtendedAdditionalBundleSet } from '../interfaces/additional-bundles.interface';

@Injectable()
export default class SlotService {

  public ordinalNumber = 0;
  public slots: SlotModel[] = [];
  public additionalBundles: IExtendedAdditionalBundleSet[] = [];
  public accessories: DeviceModel[] = [];

  private step: number = window['STEP'];
  private ordinalNumberData = 0;
  private ordinalNumberInGroup: number;
  private actionType: any;

  constructor(
    private commonAddonsService: CommonAddonsService,
    private commonGadgetsService: CommonGadgetsService,
    private notificationProvider: NotificationProviderService,
    private commonInfo: CommonInfoService,
    private dataLayerService: DataLayerService
  ) {
  }

  private getOrdinalNumberInGroup(): number {
    if (this.commonInfo.processType === ProcessTypeEnum.CART_BOHO) {
      return ++this.ordinalNumberInGroup - 1;
    } else if (this.commonInfo.isNextSim) {
      return this.ordinalNumber;
    } else {
      return this.actionType === ActionTypeEnum.VOUCHER ? null : ++this.ordinalNumberInGroup;
    }
  }

  private makeFirstStepSlot(bundle: IBundle, isInCart: boolean, slotsList: SlotModel[], title?: string) {
    switch (bundle.bundleType) {
      case BundleTypeEnum.ACCESSORY:
        bundle.device.forEach((accessory: IDevice) => {
          const accessorySlot: AccessoryFirstPageModel = new AccessoryFirstPageModel(bundle, accessory, this.commonInfo);

          this.accessories.push(accessorySlot);

          this.dataLayerService.setAccessoryData(bundle, accessory);
        });

        break;

      case BundleTypeEnum.ADDITIONAL_OFFER:
        const dataSlot = new DataSlotFirstPageModel(bundle, ++this.ordinalNumberData, isInCart, title);

        slotsList.push(dataSlot);

        this.commonGadgetsService.prepareGadgets(dataSlot);
        this.commonAddonsService.prepareSlotAddons(dataSlot);

        if (dataSlot.uid) {
          this.dataLayerService.setInternetSlotData(bundle);
        }

        break;

      default:
        const slotNumber = this.commonInfo.processType === ProcessTypeEnum.CART_BOHO ? ++this.ordinalNumber - 1 : ++this.ordinalNumber;
        const offerSlot = new SlotFirstPageModel(bundle, slotNumber, this.getOrdinalNumberInGroup());

        this.commonAddonsService.prepareSlotAddons(offerSlot);
        this.commonGadgetsService.prepareGadgets(offerSlot);

        this.slots.push(offerSlot);

        this.dataLayerService.setSlotData(bundle);
    }
  }

  private makeOtherStepSlot(bundle: IBundle, slotsList: SlotModel[], title?: string) {
    switch (bundle.bundleType) {
      case BundleTypeEnum.ACCESSORY:
        bundle.device.forEach((accessory: IDevice) => {
          const accessorySlot: AccessorySecondPageModel = new AccessorySecondPageModel(bundle, accessory, this.commonInfo);

          this.accessories.push(accessorySlot);

          if (this.step === 2 || this.step === 4) {
            this.dataLayerService.setAccessoryData(bundle, accessory);
          }
        });

        break;

      case BundleTypeEnum.ADDITIONAL_OFFER:
        const dataSlot = new DataSlotModel(bundle, ++this.ordinalNumberData, true, title);

        slotsList.push(dataSlot);

        this.commonGadgetsService.prepareGadgets(dataSlot);
        this.commonAddonsService.prepareSlotAddons(dataSlot);

        if (this.step === 4 && dataSlot.uid) {
          this.dataLayerService.setInternetSlotData(bundle);
        }

        break;

      default:
        const offerSlot = new SlotSecondPageModel(bundle, ++this.ordinalNumber, this.getOrdinalNumberInGroup());

        this.commonAddonsService.prepareSecondPageSlotAddons(offerSlot);
        this.commonGadgetsService.prepareGadgets(offerSlot);

        this.slots.push(offerSlot);

        if (this.step === 2 || this.step === 4) {
          this.dataLayerService.setSlotData(bundle);
        }
    }
  }

  private sortBundles(bundles: IBundle[]): IBundle[] {
    return bundles.sort((SlotA: IBundle, SlotB: IBundle) => {
      const noA = SlotA.slotNo;
      const noB = SlotB.slotNo;

      if (noA < noB) {
        return -1;
      }

      if (noA > noB) {
        return 1;
      }

      return 0;
    });
  }

  public prepareBundles(cartDetails: ICartDetails) {
    cartDetails.bundles = this.sortBundles(cartDetails.bundles);

    cartDetails.bundles.forEach((bundle: IBundle) => {
      if (Object.keys(bundle.offer).length) {
        this.addSlot(bundle);
      }
    });

    (cartDetails.additionalBundles || []).forEach((additionalBundle: IAdditionalBundleSet) => {
      const obj = {
        categoryId: additionalBundle.categoryId,
        header: additionalBundle.header,
        subheader: additionalBundle.subheader,
        description: additionalBundle.description,
        slots: []
      };

      this.additionalBundles.push(obj);

      additionalBundle.bundles.forEach((bundle: IBundle) => {
        this.addSlot(bundle, true, obj.slots, additionalBundle.subheader);
      });

      additionalBundle.potentialBundles.forEach((bundle: IBundle) => {
        this.addSlot(bundle, false, obj.slots, additionalBundle.subheader);
      });
    });
  }

  public setInitialData(actionType: any, currentlyProcessed: number = null): void {
    this.slots = [];
    this.additionalBundles = [];
    this.accessories = [];
    this.ordinalNumber = 0;
    this.ordinalNumberData = 100;
    this.ordinalNumberInGroup = currentlyProcessed;
    this.actionType = actionType;
    this.dataLayerService.resetSlotsData();
  }

  public hasAccessory(): any {
    return !!this.accessories.length;
  }

  public hasLeasing(): boolean {
    return this.slots.some((slot: SlotModel) => slot.isLeasing());
  }

  public hasM2mCardInCart(): boolean {
    return this.slots.some((slot: SlotModel) => slot.isM2mCard()) ||
      this.additionalBundles.some((bundle: IExtendedAdditionalBundleSet) => {
        return bundle.slots.some((slot) => slot.isInCart && slot.isM2mCard());
      });
  }

  public getSlots(): SlotModel[] {
    return this.slots;
  }

  public getGroupedSlots(): {owner: SlotModel, other: SlotModel[]} {
    const group = {
      owner: {} as SlotModel,
      other: [] as SlotModel[],
    };

    this.slots.forEach((slot: SlotModel, index: number) => {
      if (slot.isOwner) {
        group.owner = slot;
      } else {
        group.other.push(slot);
      }
    });

    return group;
  }

  public getAdditionalBundles(): IExtendedAdditionalBundleSet[] {
    return this.additionalBundles;
  }

  public getAdditionalSlots(): DataSlotFirstPageModel[] {
    return this.additionalBundles.flatMap(item => {
      return item.slots.filter(slot => slot.isInCart);
    });
  }

  public getAccessories(): DeviceModel[] {
    return this.accessories;
  }

  public addSlot(bundle: IBundle, isInCart = true, slotsList?: SlotModel[], title?: string): void {
    if (this.step === 1) {
      this.makeFirstStepSlot(bundle, isInCart, slotsList, title);
    } else {
      this.makeOtherStepSlot(bundle, slotsList, title);
    }
  }

  public slotsUpdated(): void {
    this.notificationProvider.publish(NotificationProviderTypeEnum.SLOTS_SERVICE, this);
  }

  public hasAtLeastOneDeviceInSlots(): boolean {
    return this.slots.some(slot => slot.hasDevice);
  }

  public hasAtLeastOneDeviceInAdditionalSlots(): boolean {
    return this.additionalBundles.some((bundle: IExtendedAdditionalBundleSet) => {
      return bundle.slots.some((slot) => slot.isInCart && slot.hasDevice);
    });
  }

  public hasAtLeastOneDeviceInCart(): boolean {
    return this.hasAtLeastOneDeviceInSlots() || this.hasAtLeastOneDeviceInAdditionalSlots() || !!this.accessories.length;
  }

  public findUnfinishedSlots(): SlotModel[] {
    return this.slots.filter((slot: SlotFirstPageModel) => !slot.isValid());
  }

  public findSlotByBundleNumber(bundleNumber: number): SlotModel {
    return this.slots.find(
      (slot: SlotFirstPageModel) => bundleNumber === slot.bundleNumber
    );
  }

  public getAllSlots(): SlotModel[] {
    return [...this.slots, ...this.getAdditionalSlots()];
  }
}


